import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { Patient, PatientSharedNote, addPatientSharedNote, updatePatient } from "../api/patients";

function formatDateTime(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString("ru-RU", { timeZone: "Asia/Almaty" });
}

const EYE_CHECK_ITEMS: { key: string; label: string }[] = [
  { key: "wears_glasses", label: "Носит очки" },
  { key: "eye_anatomy", label: "Анатомия глаза" },
  { key: "ptosis", label: "Опущение век" },
  { key: "cataract", label: "Наличие катаракты" },
  { key: "glaucoma", label: "Глаукома" },
  { key: "strabismus", label: "Косоглазие" },
  { key: "eye_inflammation", label: "Воспаление глаз (краснота)" },
  { key: "lacrimation", label: "Слезотечение" },
  { key: "single_lens_replacement", label: "Замена одного хрусталика" },
  { key: "both_lens_replacement", label: "Замена обоих хрусталиков" },
  { key: "optic_nerve_atrophy", label: "Атрофия зрительного нерва" },
  { key: "sleep_deprivation", label: "Недосып" },
];

interface Props {
  patient: Patient;
  onPatientUpdated: (p: Patient) => void;
}

export default function PatientNotes({ patient, onPatientUpdated }: Props) {
  const [notes, setNotes] = useState<PatientSharedNote[]>(
    (patient.shared_notes || []).slice().sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )
  );
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const [eyeChecks, setEyeChecks] = useState<Record<string, boolean>>(
    patient.eye_checks || {}
  );
  const [eyeSaving, setEyeSaving] = useState(false);
  const [eyeError, setEyeError] = useState("");
  const [eyeSuccess, setEyeSuccess] = useState(false);

  useEffect(() => {
    setNotes(
      (patient.shared_notes || []).slice().sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )
    );
    setEyeChecks(patient.eye_checks || {});
  }, [patient]);

  useEffect(() => {
    if (eyeSuccess) {
      const t = setTimeout(() => setEyeSuccess(false), 3000);
      return () => clearTimeout(t);
    }
  }, [eyeSuccess]);

  const handleAdd = async () => {
    setError("");
    setSaving(true);
    setSuccess(false);
    try {
      const trimmed = text.trim();
      if (!trimmed) {
        setError("Введите текст заметки");
        setSaving(false);
        return;
      }
      const created = await addPatientSharedNote(patient.patient_id!, trimmed);
      const updated = [created, ...notes].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setNotes(updated);
      setText("");
      setSuccess(true);
      onPatientUpdated({ ...patient, shared_notes: updated });
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Ошибка добавления заметки");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (key: string) => {
    setEyeChecks((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSaveEyeChecks = async () => {
    setEyeError("");
    setEyeSaving(true);
    try {
      const updated = await updatePatient(patient.patient_id!, { eye_checks: eyeChecks });
      onPatientUpdated(updated);
      setEyeSuccess(true);
    } catch (err: any) {
      setEyeError(err?.response?.data?.detail || "Ошибка сохранения");
    } finally {
      setEyeSaving(false);
    }
  };

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Заметки по пациенту
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Заметки видны всем пользователям
        </Typography>

        <Box sx={{ display: "flex", gap: 1, mb: 2, alignItems: "flex-start" }}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={5}
            placeholder="Введите заметку..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            size="small"
          />
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
            onClick={handleAdd}
            disabled={saving}
            sx={{ minWidth: 130, mt: 0.5 }}
          >
            {saving ? "..." : "Добавить"}
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 1 }}>Заметка сохранена</Alert>}

        {notes.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Заметок пока нет
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: 160 }}>Дата</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 150 }}>Автор</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Заметка</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notes.map((n) => (
                  <TableRow key={n.note_id}>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {formatDateTime(n.created_at)}
                    </TableCell>
                    <TableCell>{n.author_name || n.author_email || "—"}</TableCell>
                    <TableCell sx={{ whiteSpace: "pre-wrap" }}>{n.text}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
          Обязательные параметры
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0, mb: 2 }}>
          {EYE_CHECK_ITEMS.map((item) => (
            <FormControlLabel
              key={item.key}
              control={
                <Checkbox
                  checked={!!eyeChecks[item.key]}
                  onChange={() => handleToggle(item.key)}
                  size="small"
                />
              }
              label={item.label}
              sx={{ width: "50%", mr: 0 }}
            />
          ))}
        </Box>

        {eyeError && <Alert severity="error" sx={{ mb: 1 }}>{eyeError}</Alert>}
        {eyeSuccess && <Alert severity="success" sx={{ mb: 1 }}>Сохранено</Alert>}

        <Button
          variant="contained"
          startIcon={eyeSaving ? <CircularProgress size={18} /> : <SaveIcon />}
          onClick={handleSaveEyeChecks}
          disabled={eyeSaving}
        >
          Сохранить параметры
        </Button>
      </Paper>
    </Box>
  );
}
